import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { paireLangues } from "@/lib/roof";
import { useI18n } from "@/i18n/I18nContext";
import LangSwitch from "@/components/LangSwitch";
import ThemeSwitch from "@/components/ThemeSwitch";
import { LayoutDashboard, Ruler, Settings as SettingsIcon, LogOut, Users, ShieldCheck, Building2, DollarSign, Code2, MailPlus, CreditCard, FileText, SatelliteDish, FilePlus, Eye } from "lucide-react";
import Marque from "./Marque";

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [abo, setAbo] = useState(null);
  // Tiroir hamburger mobile : donne accès à toutes les rubriques de configuration.
  const [menuOuvert, setMenuOuvert] = useState(false);

  // Referme le tiroir à chaque navigation.
  useEffect(() => { setMenuOuvert(false); }, [location.pathname]);

  // Battement d'activité : une requête par minute, UNIQUEMENT quand l'onglet
  // est visible. Un onglet oublié en arrière-plan ne compte donc pas comme du
  // temps de travail. Le serveur ignore les battements rapprochés, ce qui rend
  // plusieurs onglets ouverts inoffensifs.
  // Rien n'est envoyé pendant une session de soutien : le serveur refuse les
  // POST, et le temps de l'administrateur n'entre pas dans les stats du client.
  useEffect(() => {
    if (!user || user.soutien) return;
    const battre = () => {
      if (document.visibilityState !== "visible") return;
      api.post("/activite").catch(() => {});
    };
    battre();
    const minuterie = setInterval(battre, 60_000);
    document.addEventListener("visibilitychange", battre);
    return () => {
      clearInterval(minuterie);
      document.removeEventListener("visibilitychange", battre);
    };
  }, [user]);

  // Consommation du forfait, affichee en bas de la barre laterale.
  useEffect(() => {
    if (!user) return;
    api.get("/subscription").then(({ data }) => setAbo(data)).catch(() => setAbo(null));
  }, [user, location.pathname]);

  // Paire de langues de travail du compte (ca : fr/en, us : en/es). Tant que
  // l'abonnement n'est pas chargé, on garde le défaut du sélecteur.
  const languesCompte = abo?.country ? paireLangues(abo.country) : null;

  // Si la langue d'interface courante n'appartient pas à la paire du pays
  // (ex. : compte américain avec l'interface en français), on bascule sur
  // l'anglais : il fait partie des deux paires, c'est le seul repli sûr.
  useEffect(() => {
    if (languesCompte && !languesCompte.includes(lang)) setLang("en");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abo?.country, lang]);

  const fr = lang !== "en";

  // Mesures et soumissions sont deux rubriques distinctes : certains couvreurs
  // mesurent sans jamais chiffrer. Chaque liste a son « Nouvelle » juste dessous.
  const nav = [
    { to: "/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/mesures", label: t("nav.mesures"), icon: Ruler, testid: "nav-mesures" },
    { to: "/mesures/nouvelle", label: t("nav.newMesures"), icon: SatelliteDish, testid: "nav-nouvelle-mesure" },
    { to: "/estimations", label: t("nav.quotes"), icon: FileText, testid: "nav-estimations" },
    { to: "/new", label: t("nav.newEstimate"), icon: FilePlus, testid: "nav-new-estimate" },
    { to: "/prospects", label: t("nav.prospects"), icon: Users, testid: "nav-prospects" },
  ];

  // Rubriques de configuration : chacune a son entrée directe dans le menu.
  const navReglages = [
    { to: "/settings/entreprise", label: fr ? "Entreprise" : "Company", icon: Building2, testid: "nav-entreprise" },
    { to: "/settings/prix", label: fr ? "Prix & produits" : "Pricing & products", icon: DollarSign, testid: "nav-prix" },
    { to: "/settings/soumission", label: fr ? "Modèle de soumission" : "Quote template", icon: FileText, testid: "nav-modele-soumission" },
    { to: "/settings/widget", label: "Widget", icon: Code2, testid: "nav-widget" },
    { to: "/settings/relance", label: fr ? "Relance" : "Follow-up", icon: MailPlus, testid: "nav-relance" },
    { to: "/settings/abonnement", label: fr ? "Abonnement" : "Subscription", icon: CreditCard, testid: "nav-abonnement" },
  ];

  // Visible seulement pour le super-administrateur (le serveur refuse de toute façon).
  const navAdmin = user?.role === "admin"
    ? [{ to: "/admin", label: t("nav.admin"), icon: ShieldCheck, testid: "nav-admin" }]
    : [];

  // Barre mobile : les listes seulement — les « Nouvelle » s'atteignent par le
  // bouton en tête de chaque liste, sinon la barre déborde à 8 entrées.
  const navMobile = [
    ...nav.filter((i) => !["/mesures/nouvelle", "/new"].includes(i.to)),
    { to: "/settings/entreprise", label: t("nav.pricing"), icon: SettingsIcon, testid: "nav-settings" },
    ...navAdmin,
  ];

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  // Fin de la session de soutien : rechargement complet, comme à l'entrée —
  // le cookie change, tout le cache en mémoire est périmé.
  const quitterSoutien = async () => {
    try {
      await api.post("/admin/soutien-fin");
    } catch { /* session déjà expirée : on retombe sur la connexion */ }
    window.location.href = "/admin";
  };

  return (
    <div className="min-h-screen bg-app text-ink flex">
      {/* Bandeau de soutien technique. Volontairement voyant et fixé en haut :
          consulter le compte d'un client sans s'en rendre compte serait la
          pire façon de se tromper. */}
      {user?.soutien && (
        <div
          data-testid="bandeau-soutien"
          className={`fixed top-0 inset-x-0 z-[60] px-4 py-2 flex items-center justify-center gap-3 text-sm font-semibold ${
            user.soutien_complet ? "bg-red-600 text-white" : "bg-amber-500 text-black"}`}
        >
          <Eye className="h-4 w-4 shrink-0" />
          <span className="truncate">
            {user.soutien_complet
              ? (fr
                ? `Accès complet au compte ${user.company || user.name || user.email} — vos actions sont réelles.`
                : `Full access to ${user.company || user.name || user.email} — your actions are real.`)
              : (fr
                ? `Soutien technique — vous consultez le compte de ${user.company || user.name || user.email}. Lecture seule.`
                : `Support session — you are viewing ${user.company || user.name || user.email}'s account. Read-only.`)}
          </span>
          <button
            onClick={quitterSoutien}
            data-testid="quitter-soutien"
            className="shrink-0 px-2.5 py-1 rounded-md bg-black/80 text-white text-xs font-bold hover:bg-black transition-colors"
          >
            {fr ? "Quitter" : "Exit"}
          </button>
        </div>
      )}
      {/* La barre latérale et l'en-tête mobile sont en `fixed` : le padding du
          parent ne les déplace pas, il faut les décaler explicitement sous le
          bandeau de soutien. */}
      <aside className={`hidden md:flex w-60 flex-col border-r border-line bg-surface fixed ${
        user?.soutien ? "top-10 h-[calc(100vh-2.5rem)]" : "h-screen"}`}>
        <div className="px-6 h-16 flex items-center gap-2 border-b border-line">
          <Marque taille="lg" variante="etroit" vers="/dashboard" />
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {[...nav, null, ...navReglages, ...navAdmin].map((item, idx) => {
            if (!item) {
              return (
                <p key={`sep-${idx}`} className="px-3 pt-5 pb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-ink-faint">
                  {fr ? "Configuration" : "Settings"}
                </p>
              );
            }
            const active = location.pathname === item.to ||
              (item.to === "/settings/entreprise" && location.pathname === "/settings");
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={`relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  active
                    ? "bg-brand/10 text-brand-ink"
                    : "text-ink-soft hover:bg-hover hover:text-ink"
                }`}
              >
                {/* Barre d'accent plutôt qu'un aplat orange : la navigation ne
                    doit pas crier plus fort que le contenu. */}
                {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-brand" />}
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="px-3 py-4 border-t border-line space-y-3">
          {abo && (
            <div className="px-3 pb-1">
              <div className="flex items-baseline justify-between text-xs mb-1.5">
                <span className="uppercase tracking-widest text-ink-faint">{abo.plan_nom}</span>
                <span
                  className={
                    abo.sans_forfait
                      ? "text-amber-700"
                      : abo.restant === 0
                        ? "text-red-400"
                        : "text-ink-soft"
                  }
                >
                  {/* « 0 » veut dire illimité côté serveur : sans ce test, un compte
                      non activé afficherait « ∞ » au lieu d'être signalé bloqué. */}
                  {abo.sans_forfait ? "—" : abo.limite_mensuelle === 0 ? "∞" : `${abo.utilise}/${abo.limite_mensuelle}`}
                </span>
              </div>
              {!abo.sans_forfait && abo.limite_mensuelle > 0 && (
                <div className="h-1 rounded-full bg-hover-strong overflow-hidden">
                  <div
                    className={`h-full ${abo.restant === 0 ? "bg-red-500" : "bg-orange-500"}`}
                    style={{ width: `${Math.min(100, (abo.utilise / abo.limite_mensuelle) * 100)}%` }}
                  />
                </div>
              )}
            </div>
          )}
          <div className="px-3 flex items-center gap-2">
            <LangSwitch langues={languesCompte || undefined} />
            <ThemeSwitch />
          </div>
          <div className="px-3 py-1">
            <p className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name || "Roofer"}</p>
            <p className="text-xs text-ink-faint truncate">{user?.email}</p>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-button"
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-ink-soft hover:bg-hover-strong hover:text-ink transition-colors"
          >
            <LogOut className="h-4 w-4" /> {t("nav.signOut")}
          </button>
        </div>
      </aside>

      <div className={`md:hidden fixed inset-x-0 z-40 flex items-center justify-between px-4 h-14 border-b border-line bg-app/85 backdrop-blur-xl ${
        user?.soutien ? "top-10" : "top-0"}`}>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMenuOuvert((v) => !v)}
            data-testid="mobile-hamburger"
            className="p-1.5 -ml-1.5 text-ink-soft"
            aria-label="Menu"
          >
            {menuOuvert ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <Marque taille="md" variante="etroit" vers="/dashboard" />
        </div>
        <div className="flex items-center gap-3">
          {abo && (
            <span className="text-xs text-ink-faint">
              {abo.limite_mensuelle === 0 ? "∞" : `${abo.utilise}/${abo.limite_mensuelle}`}
            </span>
          )}
          <LangSwitch langues={languesCompte || undefined} />
          <button onClick={doLogout} className="text-ink-soft" aria-label={t("nav.signOut")}>
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Tiroir mobile : toutes les sections, y compris les rubriques de configuration. */}
      {menuOuvert && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMenuOuvert(false)} />
          <div className="absolute top-0 left-0 bottom-0 w-72 max-w-[85vw] bg-surface border-r border-line overflow-y-auto">
            <div className="px-5 h-14 flex items-center justify-between border-b border-line">
              <Marque taille="md" variante="etroit" vers="/dashboard" />
              <button onClick={() => setMenuOuvert(false)} className="p-1.5 text-ink-soft" aria-label="Fermer">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="px-3 py-4 space-y-1">
              {[...nav, null, ...navReglages, ...navAdmin].map((item, idx) => {
                if (!item) {
                  return (
                    <p key={`msep-${idx}`} className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-ink-faint">
                      {fr ? "Configuration" : "Settings"}
                    </p>
                  );
                }
                const active = location.pathname === item.to ||
                  (item.to === "/settings/entreprise" && location.pathname === "/settings");
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`${item.testid}-drawer`}
                    className={`relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                      active ? "bg-brand/10 text-brand-ink" : "text-ink-soft hover:bg-hover"
                    }`}
                  >
                    {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-brand" />}
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            <div className="px-6 py-4 mt-2 border-t border-line flex items-center gap-2">
              <ThemeSwitch />
              <LangSwitch langues={languesCompte || undefined} />
            </div>
          </div>
        </div>
      )}

      {/* Navigation mobile : barre d'onglets en bas, en icones — le menu en texte
          debordait des l'ajout des sections Prospects et Administration. */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 flex border-t border-line bg-app/90 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]">
        {navMobile.map((item) => {
          const active = item.to.startsWith("/settings")
            ? location.pathname.startsWith("/settings")
            : location.pathname === item.to;
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`${item.testid}-mobile`}
              className={`flex-1 flex flex-col items-center justify-center gap-1 py-2.5 min-h-[56px] text-[10px] leading-tight text-center px-1 ${
                active ? "text-brand-ink" : "text-ink-faint"
              }`}
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span className="truncate w-full">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <main className={`flex-1 md:ml-60 pt-14 md:pt-0 pb-20 md:pb-0 min-h-screen ${
        user?.soutien ? "mt-10" : ""}`}>{children}</main>
    </div>
  );
}
